import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryKey } from '@/Config/api/queryKey';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const OTHER_TEXT_DEBOUNCE_MS = 800;

/**
 * GettingStarted onboarding state, backed by the
 * `/v1/api/getting_started_progress` endpoint.
 *
 * - Progress is scoped per `ChatImplementation`; each new agent a user creates
 *   gets a fresh onboarding. Absence of a row (404 -> `data === null`) signals
 *   a legacy implementation that was minted before the flow existed — the
 *   route guard treats those as already complete.
 * - AI-agent name and prompt intentionally do not live here: the Identity step
 *   writes directly to `KnowledgeBase.one_word_description` /
 *   `short_description`. This hook only persists *that the user finished the
 *   Identity step*, not the values.
 * - Step IDs in the UI (`gs_step_1..N`) are decoupled from the DB's semantic
 *   field names so the checklist can be reordered without migrating data.
 */

// Source of truth for the GettingStarted checklist. Edit this list to
// add/remove/reorder steps. Each ID must have matching `steps.<id>.title` and
// `steps.<id>.description` entries in every translation file, plus a mapping
// in `STEP_SEMANTIC_NAME` and `STEP_COMPLETED_FIELD` below.
export const GS_STEP_IDS = [
  'gs_step_1',
  'gs_step_2',
  'gs_step_3',
  'gs_step_4',
  'gs_step_5',
] as const;
export type GsStepId = (typeof GS_STEP_IDS)[number];

/**
 * Onboarding mode, persisted on the progress row:
 *   - `automatic`: default / the URL-first screen and the AI-driven flow behind it
 *   - `manual`:    user clicked Skip on the URL-first screen — the 4-step manual
 *                  checklist takes over instead
 *
 * The old three-value enum had a separate `choice` state for "user hasn't
 * picked yet", but now that automatic and manual share the same first screen
 * (the URL form, with Skip branching off to manual) there's no pre-choice
 * phase in the UI. `normalizeGsMode` below coerces legacy `choice` rows
 * coming back from the backend so old data still renders correctly — we
 * never write `choice` from the frontend.
 */
export type GsMode = 'automatic' | 'manual';

/** What the backend might actually return for `mode`, including legacy rows. */
type GsModeDb = GsMode | 'choice';

/**
 * Coerce the raw DB value into the narrower UI type. `choice` → `automatic`
 * because both now render the same URL-first screen and the default for new
 * rows is effectively "let the user pick a branch from that screen".
 */
function normalizeGsMode(raw: GsModeDb | undefined): GsMode {
  return raw === 'manual' ? 'manual' : 'automatic';
}

export interface GettingStartedChecklistItem {
  id: GsStepId;
  title: string;
  /**
   * Plain-text description shown when the step is expanded. Ignored when
   * `expandedContent` is supplied — steps with custom UI replace the description.
   */
  description: string;
  completed: boolean;
  skipped: boolean;
  /**
   * Optional custom node rendered in place of `description` when the step is
   * expanded. Used by steps that need an inline form (e.g. Identity).
   */
  expandedContent?: ReactNode;
  /**
   * When set, the checklist replaces the default "Skip step" button with a
   * primary button labeled with this string. Clicking it marks the step as
   * complete (not skipped). Used by steps where progressing forward is the
   * expected action (e.g. Identity → Continue).
   */
  primaryActionLabel?: string;
  /**
   * When true, the checklist renders a "Back" text link next to the primary
   * button. By default it un-completes the previous step in the list and
   * re-expands it; supply `onBackAction` to override that behavior (e.g. on
   * the first step, where "Back" should return to the URL-first entry screen).
   */
  showBackAction?: boolean;
  /**
   * Optional override for the Back button's click handler. When provided, the
   * checklist calls this instead of un-completing the previous step. Used by
   * the first step to bail out of the checklist entirely.
   */
  onBackAction?: () => void;
  /**
   * Optional override for the primary button's click handler. When provided,
   * the checklist calls this instead of marking the step complete — the step
   * owner is then responsible for running any side effects (e.g. firing a
   * mutation) and advancing the flow. Leave unset for the default behavior.
   *
   * May return a Promise; while it's pending, the checklist disables the
   * Continue/Finish button and shows a spinner so the click can't double-fire
   * during async validation/mutation work.
   */
  onPrimaryAction?: () => void | Promise<void>;
}

type SemanticStepName = 'identity' | 'knowledge_base' | 'external_apps' | 'deployment' | 'pricing';

export interface GettingStartedProgressDto {
  // Incoming DB value may include the legacy `choice`; UI consumers should
  // always read `mode` off the hook (which normalizes) rather than off this
  // raw DTO.
  mode: GsModeDb;
  identity_step_completed: boolean;
  knowledge_base_step_completed: boolean;
  external_apps_step_completed: boolean;
  deployment_step_completed: boolean;
  pricing_step_completed: boolean;
  skipped_steps: SemanticStepName[];
  automatic_flow_completed: boolean;
  external_apps_selection: string[];
  deployment_selection: string[];
  external_apps_other_text: string;
  deployment_other_text: string;
}

type CompletedField =
  | 'identity_step_completed'
  | 'knowledge_base_step_completed'
  | 'external_apps_step_completed'
  | 'deployment_step_completed'
  | 'pricing_step_completed';

const STEP_SEMANTIC_NAME: Record<GsStepId, SemanticStepName> = {
  gs_step_1: 'identity',
  gs_step_2: 'knowledge_base',
  gs_step_3: 'external_apps',
  gs_step_4: 'deployment',
  gs_step_5: 'pricing',
};

const STEP_COMPLETED_FIELD: Record<GsStepId, CompletedField> = {
  gs_step_1: 'identity_step_completed',
  gs_step_2: 'knowledge_base_step_completed',
  gs_step_3: 'external_apps_step_completed',
  gs_step_4: 'deployment_step_completed',
  gs_step_5: 'pricing_step_completed',
};

function useProgressQuery() {
  return useQuery<GettingStartedProgressDto | null>({
    queryKey: [QueryKey.gettingStarted.progress],
  });
}

/**
 * Route-guard helper used by `DefaultLayout`. Legacy implementations (no row)
 * are treated as already complete — they skip the Getting Started flow.
 */
export function useIsGettingStartedComplete(): {
  isLoading: boolean;
  isComplete: boolean;
} {
  const { data, isLoading } = useProgressQuery();
  if (isLoading) return { isLoading: true, isComplete: false };
  if (!data) return { isLoading: false, isComplete: true };
  const mode = normalizeGsMode(data.mode);
  if (mode === 'manual') {
    const allDone = GS_STEP_IDS.every((id) => {
      const completedField = STEP_COMPLETED_FIELD[id];
      const semantic = STEP_SEMANTIC_NAME[id];
      return data[completedField] === true || data.skipped_steps.includes(semantic);
    });
    return { isLoading: false, isComplete: allDone };
  }
  // `automatic` (the default): completion hinges on the scrape-and-post-step
  // wizard having been walked to its Finish button.
  return { isLoading: false, isComplete: data.automatic_flow_completed };
}

export interface UseGettingStartedOnboardingResult {
  data: GettingStartedProgressDto | null;
  isLoading: boolean;
  steps: { id: GsStepId; completed: boolean; skipped: boolean }[];
  completed_count: number;
  total_count: number;
  mode: GsMode;
  isAutomaticComplete: boolean;
  isSkipPending: boolean;
  externalAppsSelection: Set<string>;
  deploymentSelection: Set<string>;
  externalAppsOtherText: string;
  deploymentOtherText: string;
  skipStep: (stepId: GsStepId, skip: boolean) => void;
  completeStep: (stepId: GsStepId, complete: boolean) => void;
  setMode: (mode: GsMode) => void;
  setAutomaticComplete: (complete: boolean) => void;
  toggleExternalApp: (appId: string) => void;
  toggleDeploymentChannel: (channelId: string) => void;
  setExternalAppsOtherText: (text: string) => void;
  setDeploymentOtherText: (text: string) => void;
}

export function useGettingStartedOnboarding(): UseGettingStartedOnboardingResult {
  const queryClient = useQueryClient();
  const { data = null, isLoading } = useProgressQuery();
  const { capture } = usePostHogAnalytics();

  const mutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>,
    { previous: GettingStartedProgressDto | null | undefined }
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    // Optimistic update: apply the delta to the cache immediately so the UI
    // reflects the user's action before the PATCH round-trips. Mirrors the KB
    // autosave pattern in IdentityStep.tsx.
    onMutate: (delta) => {
      const previous = queryClient.getQueryData<GettingStartedProgressDto | null>([
        QueryKey.gettingStarted.progress,
      ]);
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...delta } : prev),
      );
      return { previous };
    },
    // Merge only the fields this mutation owns, instead of overwriting the
    // cache with the full server response. Two concurrent PATCHes can each
    // serialize the row they read *before* the other committed — so the
    // response of a slower PATCH holds stale values for fields the other
    // PATCH just updated. Overwriting with that response regresses cache
    // state (e.g. user clicks Continue on external-apps and Finish on
    // deployment in quick succession; the external-apps response lands
    // after the Finish PATCH's optimistic update and flips
    // `automatic_flow_completed` back to false, bouncing the user from
    // AI Preview back to /getting-started via the route guard).
    onSuccess: (_updated, variables) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => {
          if (!prev) return prev;
          const merged = { ...prev, ...variables };
          // The optimistic setter is authoritative for the debounced text fields —
          // a stale `variables` snapshot from an older PATCH must not revert
          // newer keystrokes the user has already typed.
          merged.external_apps_other_text = prev.external_apps_other_text;
          merged.deployment_other_text = prev.deployment_other_text;
          return merged;
        },
      );
    },
    onError: (_err, _vars, context) => {
      if (context) {
        queryClient.setQueryData([QueryKey.gettingStarted.progress], context.previous);
      }
    },
  });

  const steps = useMemo(
    () =>
      GS_STEP_IDS.map((id) => {
        const completedField = STEP_COMPLETED_FIELD[id];
        const semantic = STEP_SEMANTIC_NAME[id];
        return {
          id,
          completed: data?.[completedField] ?? false,
          skipped: data?.skipped_steps?.includes(semantic) ?? false,
        };
      }),
    [data],
  );

  // Skipped counts toward "done" for the progress bar — same UX as the
  // AI Preview, so the bar fills up as the user makes decisions.
  const completed_count = useMemo(
    () => steps.filter((s) => s.completed || s.skipped).length,
    [steps],
  );

  const skipStep = useCallback(
    (stepId: GsStepId, skip: boolean) => {
      if (!data) return;
      const semantic = STEP_SEMANTIC_NAME[stepId];
      const next = skip
        ? Array.from(new Set([...data.skipped_steps, semantic]))
        : data.skipped_steps.filter((name) => name !== semantic);
      mutation.mutate({ skipped_steps: next });
      // Only fire on an actual skip — `skip: false` is an unskip (today only
      // reachable from internal test paths, but keep the event semantics tight).
      if (skip) {
        capture(PostHogEventEnum.GettingStartedStepSkipped, {
          step_id: stepId,
          step_name: semantic,
        });
      }
    },
    [data, mutation, capture],
  );

  const completeStep = useCallback(
    (stepId: GsStepId, complete: boolean) => {
      mutation.mutate({ [STEP_COMPLETED_FIELD[stepId]]: complete });
      // `complete: false` is the Back button un-completing the previous step
      // so the user can revisit it. That's not a conversion signal — skip it.
      if (complete) {
        capture(PostHogEventEnum.GettingStartedStepCompleted, {
          step_id: stepId,
          step_name: STEP_SEMANTIC_NAME[stepId],
        });
      }
    },
    [mutation, capture],
  );

  const setMode = useCallback(
    (mode: GsMode) => {
      // Bundle the automatic-complete reset into the same PATCH when leaving
      // the automatic flow. Splitting it across two `mutation.mutate` calls
      // produces a race: each call has the same `mutationKey`, so the first
      // PATCH's `onSuccess` returns the server state that still has the
      // old `mode` and overwrites the optimistic `mode: 'manual'` cache,
      // bouncing the UI back to the URL-first screen.
      const delta: Partial<GettingStartedProgressDto> = { mode };
      if (mode !== 'automatic') delta.automatic_flow_completed = false;
      mutation.mutate(delta);
    },
    [mutation],
  );

  const setAutomaticComplete = useCallback(
    (complete: boolean) => {
      mutation.mutate({ automatic_flow_completed: complete });
    },
    [mutation],
  );

  const toggleExternalApp = useCallback(
    (appId: string) => {
      if (!data) return;
      const next = new Set(data.external_apps_selection);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      mutation.mutate({ external_apps_selection: Array.from(next) });
    },
    [data, mutation],
  );

  const toggleDeploymentChannel = useCallback(
    (channelId: string) => {
      if (!data) return;
      const next = new Set(data.deployment_selection);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      mutation.mutate({ deployment_selection: Array.from(next) });
    },
    [data, mutation],
  );

  const mutateRef = useRef(mutation);
  mutateRef.current = mutation;
  const debouncedPatchExternalAppsOtherText = useMemo(
    () =>
      debounce(
        (text: string) => mutateRef.current.mutate({ external_apps_other_text: text }),
        OTHER_TEXT_DEBOUNCE_MS,
      ),
    [],
  );
  const debouncedPatchDeploymentOtherText = useMemo(
    () =>
      debounce(
        (text: string) => mutateRef.current.mutate({ deployment_other_text: text }),
        OTHER_TEXT_DEBOUNCE_MS,
      ),
    [],
  );
  // Flush on unmount so a Continue-mid-typing click still persists the typed value.
  useEffect(
    () => () => {
      debouncedPatchExternalAppsOtherText.flush();
      debouncedPatchDeploymentOtherText.flush();
    },
    [debouncedPatchExternalAppsOtherText, debouncedPatchDeploymentOtherText],
  );

  const setExternalAppsOtherText = useCallback(
    (text: string) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, external_apps_other_text: text } : prev),
      );
      debouncedPatchExternalAppsOtherText(text);
    },
    [queryClient, debouncedPatchExternalAppsOtherText],
  );

  const setDeploymentOtherText = useCallback(
    (text: string) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, deployment_other_text: text } : prev),
      );
      debouncedPatchDeploymentOtherText(text);
    },
    [queryClient, debouncedPatchDeploymentOtherText],
  );

  // Memoize the selection Sets so their identity only changes when the
  // underlying array changes. A fresh `new Set(...)` on every render caused
  // downstream effects depending on these (e.g. the completion-navigate
  // effect in GettingStarted.page.tsx) to re-run on unrelated re-renders,
  // and their cleanup functions cancelled in-flight setTimeouts — which is
  // how a user ended up stuck on a blank screen after finishing onboarding.
  const externalAppsSelection = useMemo(
    () => new Set(data?.external_apps_selection ?? []),
    [data?.external_apps_selection],
  );
  const deploymentSelection = useMemo(
    () => new Set(data?.deployment_selection ?? []),
    [data?.deployment_selection],
  );

  return {
    data,
    isLoading,
    steps,
    completed_count,
    total_count: GS_STEP_IDS.length,
    mode: normalizeGsMode(data?.mode),
    isAutomaticComplete: data?.automatic_flow_completed ?? false,
    isSkipPending: mutation.isPending,
    externalAppsSelection,
    deploymentSelection,
    externalAppsOtherText: data?.external_apps_other_text ?? '',
    deploymentOtherText: data?.deployment_other_text ?? '',
    skipStep,
    completeStep,
    setMode,
    setAutomaticComplete,
    toggleExternalApp,
    toggleDeploymentChannel,
    setExternalAppsOtherText,
    setDeploymentOtherText,
  };
}
